import type { ReactNode } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Link } from '@liminal/web-ui/primitives/Link';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { formatOrderDate } from '@liminal/utils/date';
import { truncateMiddle } from '@liminal/utils/truncate';
import type { RewardPayment } from '@/api/types';
import { paymentState, redemptionState } from '@/components/ui/statusHelpers';
import { TransactionIdLink } from '@/components/ui/TransactionIdLink';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { ADMIN_ENV, type AdminEnv } from '@/lib/env';
import { groupDecimal, isPositive, usdText } from '@/lib/rewards';

interface PaymentDetailsProps {
  readonly payment: RewardPayment | null;
  readonly merchantName: string;
  readonly onClose: () => void;
}

// The transaction page of the chain each console's Partner Rewards pays on, as
// `transaction_explorer_url` in configuration/data/chains/: Base for dev and
// prod (base.json), Base Sepolia for testnet (base_sepolia.json), as
// product/rewards/partner_service/config/ chooses.
const TX_EXPLORERS: Readonly<Record<AdminEnv, string>> = {
  dev: 'https://basescan.org/tx/',
  testnet: 'https://sepolia.basescan.org/tx/',
  prod: 'https://basescan.org/tx/',
};
const TX_EXPLORER = TX_EXPLORERS[ADMIN_ENV];

function ChainLink({ hash }: { readonly hash?: string }) {
  if (!hash) return <>Not yet</>;
  return (
    <Link href={`${TX_EXPLORER}${hash}`} external>
      {truncateMiddle(hash)}
    </Link>
  );
}

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>{children}</Box>
    </Stack>
  );
}

/** One reward payment in full: what the customer paid, what they were offered, what they chose and where it got to. */
export function PaymentDetails({ payment, merchantName, onClose }: PaymentDetailsProps) {
  if (!payment) return null;
  const state = paymentState(payment.state);
  const redemption = payment.redemption;
  const step = redemption ? redemptionState(redemption.state) : null;
  // At a revision with Pay with LIL levels the payment offers levels, never
  // the whole payment; the redemption names the level it took by position
  // (zero, which the gateway leaves out, for a whole-payment one).
  const payOptions = payment.payOptions ?? [];
  const levelTaken = redemption?.payLevel ? payment.rates?.payLevels?.[redemption.payLevel - 1] : undefined;
  if (redemption?.payLevel && !levelTaken) {
    throw new Error(`payment ${payment.name} took pay level ${redemption.payLevel}, which its rates do not carry`);
  }

  return (
    <Modal open onClose={onClose} title={`${merchantName} payment`} width="md" dismissible>
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusPill tone={state.tone} label={state.label} />
            {step && <StatusPill tone={step.tone} label={step.label} size="sm" />}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {state.description}
          </Typography>
        </Stack>

        <Section title="The payment">
          <DetailRow layout="stacked" label="Customer" value={payment.userId ? <UserIdLink userId={payment.userId} /> : '—'} />
          <DetailRow layout="stacked" label="Settled" value={payment.settleTime ? formatOrderDate(payment.settleTime) : '—'} />
          <DetailRow layout="stacked" label="Paid" value={usdText(payment.paidUsd)} />
          <DetailRow
            layout="stacked"
            label="Refunded"
            value={
              payment.refundedUsd === undefined ? '—' : isPositive(payment.refundedUsd) ? usdText(payment.refundedUsd) : 'Nothing'
            }
          />
        </Section>

        <Divider />

        <Section title="What the customer was offered">
          <DetailRow
            layout="stacked"
            label="Pay with LIL"
            value={
              payOptions.length > 0 ? (
                <Stack spacing={0.5}>
                  {payOptions.map((option) => (
                    <span key={option.position}>
                      {`${option.lil ? `${groupDecimal(option.lil)} LIL` : '—'} for ${usdText(option.usdBack)} back`}
                    </span>
                  ))}
                </Stack>
              ) : payment.redeemLil === undefined ? (
                '—'
              ) : isPositive(payment.redeemLil) ? (
                `${groupDecimal(payment.redeemLil)} LIL for ${usdText(payment.coveredUsd)} back`
              ) : (
                'Nothing left to cover'
              )
            }
          />
          <DetailRow
            layout="stacked"
            label="Or earn"
            value={payment.earnAvailable && isPositive(payment.earnLil) ? `${groupDecimal(payment.earnLil)} LIL` : 'Not offered'}
          />
          <DetailRow
            layout="stacked"
            label="Rates used"
            value={
              payment.rates
                ? `Pay ${groupDecimal(payment.rates.redeemLilPerUsd)} · earn ${groupDecimal(payment.rates.earnLilPerUsd)} LIL per $1`
                : '—'
            }
          />
        </Section>

        {redemption && (
          <>
            <Divider />
            <Section title="Pay with LIL">
              <DetailRow
                layout="stacked"
                label="Level chosen"
                value={
                  levelTaken
                    ? `Level ${redemption.payLevel}: ${usdText(levelTaken.usd)}`
                    : 'The whole payment (no levels)'
                }
              />
              <DetailRow
                layout="stacked"
                label="LIL collected"
                value={redemption.lil ? `${groupDecimal(redemption.lil)} LIL` : '—'}
              />
              <DetailRow layout="stacked" label="USD back" value={usdText(redemption.usdBack)} />
              <DetailRow
                layout="stacked"
                label="LIL taken from customer"
                value={
                  <Stack spacing={0.5}>
                    <ChainLink hash={redemption.collectTransactionHash} />
                    {redemption.collectTransactionId && <TransactionIdLink transactionId={redemption.collectTransactionId} />}
                  </Stack>
                }
              />
              <DetailRow
                layout="stacked"
                label="Money sent to customer"
                value={
                  <Stack spacing={0.5}>
                    <ChainLink hash={redemption.reimburseTransactionHash} />
                    {redemption.reimburseTransactionId && <TransactionIdLink transactionId={redemption.reimburseTransactionId} />}
                  </Stack>
                }
              />
              <DetailRow
                layout="stacked"
                label="Last moved"
                value={redemption.updateTime ? formatOrderDate(redemption.updateTime) : '—'}
              />
            </Section>
          </>
        )}

        <Divider />

        <Section title="References">
          <DetailRow layout="stacked" label="Pix payment (ledger)" value={<TransactionIdLink transactionId={payment.uid} />} />
          {payment.grantId && <DetailRow layout="stacked" label="Earn grant" value={<CopyableId value={payment.grantId} />} />}
          {redemption?.uid && <DetailRow layout="stacked" label="Redemption" value={<CopyableId value={redemption.uid} />} />}
          <DetailRow layout="stacked" label="Partner revision" value={payment.merchantRevisionId ?? '—'} />
        </Section>
      </Stack>
    </Modal>
  );
}
